import constate from 'constate';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ContactGroupType } from 'contacts/enums';
import { useContactGroupActions, useGetContactDetails } from 'contacts/hooks';
import { IContact, IContactDetails, IContactGroup } from 'contacts/types';
import { getEmailDomain } from 'contacts/utils';
import { NotificationPosition } from 'core/enums';
import { useNotificationContext } from 'core/states';
import { GetOneResult } from 'core/types';

import { ContactGroupMode } from './enums';
import { mapContactGroupForm } from './Steps/ContactGroupDetails/AddContactGroup';

interface IProps {
  onClose: () => void;
  contactGroupType: ContactGroupType;
  searchContact?: boolean;
  onSearchCustomerSuccess?: (contact: IContact, contactGroup?: IContactGroup) => void;
}

const useAddContactGroupByEmail = ({
  onClose,
  contactGroupType = ContactGroupType.CUSTOMER,
  searchContact = false,
  onSearchCustomerSuccess,
}: IProps) => {
  const [contactEmail, setContactEmail] = useState<string | null>(null);
  const [genericEmail, setGenericEmail] = useState<boolean>(false);

  const [contact, setContact] = useState<IContact | null>(null);
  const [selectedContact, setSelectedContact] = useState<IContact | null>(null);
  const [contactGroup, setContactGroup] = useState<IContactGroup | null>(null);
  const [contactGroupLocked, setContactGroupLocked] = useState<boolean>(false);
  const [contactGroupMode, setContactGroupMode] = useState<ContactGroupMode>(
    ContactGroupMode.CREATE_NEW
  );

  // The domain the user confirmed attaching, not a plain flag: the contact's email stays editable
  // in step 4, so only a save that keeps this exact domain may carry the attach.
  const [attachDomainConfirmed, setAttachDomainConfirmed] = useState<string | null>(null);
  const [attachDomainConfirmation, setAttachDomainConfirmation] = useState<boolean>(false);

  const [contactDeletedWarning, setContactDeletedWarning] = useState<boolean>(false);
  const [contactFromOtherContactGroupType, setContactFromOtherContactGroupType] =
    useState<boolean>(false);

  const [contactDetailsPromise, setContactDetailsPromise] = useState<{
    resolve: (value: boolean) => void;
  } | null>(null);
  const [attachDomainPromise, setAttachDomainPromise] = useState<{
    resolve: (value: boolean) => void;
  } | null>(null);

  const contactEmailRef = useRef(null);
  const contactGroupRef = useRef(null);

  const { t } = useTranslation();

  const { showErrorNotification } = useNotificationContext();

  const { upsertContactGroup } = useContactGroupActions();

  useGetContactDetails(
    {
      contact: { email: contactEmail },
    },
    {
      enabled: Boolean(contactDetailsPromise),
      onSuccess: (data: GetOneResult<IContactDetails>) => {
        const contact = data?.record?.contact || null;
        const contactGroup =
          data?.record?.contactGroup || data?.record?.suggestedContactGroup || null;

        const genericEmail = data?.record?.emailGeneric || false;
        const unmatchedGenericEmail = !contactGroup?.id && genericEmail;

        setContact(contact);
        setSelectedContact(contact);
        setContactGroup(contactGroup);
        setGenericEmail(unmatchedGenericEmail);
        setContactGroupLocked(Boolean(contactGroup?.id));
        setContactGroupMode(
          unmatchedGenericEmail ? ContactGroupMode.SELECT_EXISTING : ContactGroupMode.CREATE_NEW
        );
        setAttachDomainConfirmed(null);

        if (contactGroup?.id) {
          if (contactGroupType === ContactGroupType.CUSTOMER && !contactGroup?.isCustomer) {
            setContactFromOtherContactGroupType(true);

            contactDetailsPromise.resolve(false);
            setContactDetailsPromise(null);

            return;
          }

          if (contactGroupType === ContactGroupType.VENDOR && !contactGroup?.isVendor) {
            setContactFromOtherContactGroupType(true);

            contactDetailsPromise.resolve(false);
            setContactDetailsPromise(null);

            return;
          }
        }

        const deletedContact = Boolean(contact?.deleted);

        if (deletedContact) {
          setContactDeletedWarning(true);

          return false;
        }

        contactDetailsPromise.resolve(true);

        setContactDetailsPromise(null);
      },
    }
  );

  const onContactEmailSubmit = useCallback(async (): Promise<boolean | string> => {
    try {
      const formResult = await contactEmailRef.current.triggerFormSubmit();

      setContactEmail(formResult.email);

      return new Promise((resolve) => {
        setContactDetailsPromise({ resolve });
      });
    } catch {
      return false;
    }
  }, []);

  const onContactEmailDeletedConfirm = useCallback(() => {
    contactDetailsPromise.resolve(true);

    setContactDetailsPromise(null);
    setContactDeletedWarning(false);
  }, [contactDetailsPromise]);

  const onContactEmailDeletedCancel = useCallback(() => {
    contactDetailsPromise.resolve(false);

    setContactEmail(null);
    setContact(null);
    setContactGroup(null);
    setContactGroupLocked(false);
    setContactGroupMode(ContactGroupMode.CREATE_NEW);
    setAttachDomainConfirmed(null);

    contactEmailRef.current.setContactEmail(null);

    setContactDetailsPromise(null);
    setContactDeletedWarning(false);
  }, [contactDetailsPromise]);

  const onContactGroupChoose = (option: IContactGroup) => {
    setContactGroup(option);
    setAttachDomainConfirmed(null);
  };

  const onContactGroupModeChange = (mode: ContactGroupMode) => {
    setContactGroupMode(mode);
    setContactGroup(null);
    setAttachDomainConfirmed(null);
  };

  const onContactGroupSubmit = useCallback(async (): Promise<boolean | string> => {
    if (contactGroupLocked) {
      return true;
    }

    if (contactGroupMode === ContactGroupMode.SELECT_EXISTING) {
      if (!contactGroup?.id) {
        showErrorNotification(
          {
            title: t('titleValidationError'),
            message: t('messageSelectContactGroup'),
          },
          NotificationPosition.DRAWER
        );

        return false;
      }

      if (genericEmail) {
        return true;
      }

      // The chosen company doesn't own the contact's email domain, so saving the contact will also
      // append it (`attachEmailDomain`) — an extra confirmation gates that side effect.
      setAttachDomainConfirmation(true);

      return new Promise((resolve) => {
        setAttachDomainPromise({ resolve });
      });
    }

    try {
      const formResult = await contactGroupRef.current?.triggerFormSubmit();

      const { success, contactGroup } = await upsertContactGroup(mapContactGroupForm(formResult));

      if (!success) {
        return false;
      }

      setContactGroup(contactGroup);
      // The company now exists — coming back to this step must show it, not re-create it.
      setContactGroupLocked(true);

      return true;
    } catch {
      return false;
    }
  }, [
    contactGroup?.id,
    contactGroupLocked,
    contactGroupMode,
    genericEmail,
    upsertContactGroup,
    showErrorNotification,
    t,
  ]);

  const onAttachDomainConfirm = useCallback(() => {
    setAttachDomainConfirmed(getEmailDomain(contactEmail));
    setAttachDomainConfirmation(false);

    attachDomainPromise?.resolve(true);
    setAttachDomainPromise(null);
  }, [attachDomainPromise, contactEmail]);

  const onAttachDomainCancel = useCallback(() => {
    setAttachDomainConfirmed(null);
    setAttachDomainConfirmation(false);

    attachDomainPromise?.resolve(false);
    setAttachDomainPromise(null);
  }, [attachDomainPromise]);

  const onSearchCustomerSubmit = useCallback(() => {
    if (!searchContact) {
      return true;
    }

    if (!selectedContact) {
      showErrorNotification(
        {
          title: t('titleValidationError'),
          message: t('messageFindCustomerNoSelectedContact'),
        },
        NotificationPosition.DRAWER
      );

      return false;
    }

    onSearchCustomerSuccess?.(selectedContact, contactGroup);

    return true;
  }, [
    searchContact,
    selectedContact,
    onSearchCustomerSuccess,
    contactGroup,
    showErrorNotification,
    t,
  ]);

  const handleOnClose = () => {
    setContactEmail(null);
    setContact(null);
    setSelectedContact(null);
    setContactGroup(null);
    setContactGroupLocked(false);
    setContactGroupMode(ContactGroupMode.CREATE_NEW);

    setGenericEmail(false);
    setContactDeletedWarning(false);
    setContactFromOtherContactGroupType(false);

    setAttachDomainConfirmed(null);
    setAttachDomainConfirmation(false);

    setContactDetailsPromise(null);
    setAttachDomainPromise(null);

    onClose();
  };

  return {
    searchContact,

    contactGroupType,

    contactEmailRef,
    contactGroupRef,

    contactEmail,
    genericEmail,
    contact,
    selectedContact,
    contactGroup,
    contactGroupLocked,
    contactGroupMode,

    contactDeletedWarning,
    contactFromOtherContactGroupType,

    attachDomainConfirmed,
    attachDomainConfirmation,

    setSelectedContact,

    onContactEmailSubmit,
    onContactEmailDeletedConfirm,
    onContactEmailDeletedCancel,
    onContactGroupChoose,
    onContactGroupModeChange,
    onContactGroupSubmit,
    onAttachDomainConfirm,
    onAttachDomainCancel,
    onSearchCustomerSubmit,

    onClose: handleOnClose,
  };
};

export const [AddContactGroupByEmailProvider, useAddContactGroupByEmailContext] =
  constate(useAddContactGroupByEmail);
